import {
	CancelOperationAlert,
	ErrorFillOperationAlert,
	OperationUnfilled,
	SkippedOperationsAlertProduct,
} from '@/entities';
import { CalendarSinglePicker, CustomSelect, useAppSelector } from '@/shared';
import styles from '@/shared/components/custom-select/custom-select.module.scss';
import { selectCurrentUserId } from '@/shared/state/slices';
import {
	AssemblyTechMapResponse,
	PreformTechMapResponse,
	ProductTechMapResponse,
	RubberTechMapResponse,
} from '@/shared/state/api/swagger';
import { useOperationNotification } from '@/widgets/operation/hooks';
import { cancelOperationSchema } from '@/widgets/operation/model/operations.schema';
import { Button, chakra, Text } from '@chakra-ui-kraud/react';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { OperationLayout } from '../../operation-layout/operation-layout';
import { OperationFormHeader } from '../form-header/form-header';
import {
	BaseCancelOperationFormProps,
	CancelOperationConfig,
	CancelOperationFormValues,
} from './cancel-operation-form.types';

export function CancelOperationFormBase<TOperation, TTechMap, TCancelRequest>({
	skippedOperations,
	setSkippedOperations,
	techMap,
	mappedEmployees,
	config,
}: BaseCancelOperationFormProps<TOperation, TTechMap> & {
	config: CancelOperationConfig<TOperation, TTechMap, TCancelRequest>;
}) {
	const [step, setStep] = useState(0);
	const [preview, setPreview] = useState(true);
	const [error, setError] = useState(false);

	const operation = skippedOperations[step];

	const [cancelOperation] = config.useCancelMutation();
	const { updateOperations } = config.useUpdateOperations(config.getTechMapId(techMap));
	const [groupVisibility, setGroupVisibility] = useState(false);
	const currentUserId = useAppSelector(selectCurrentUserId);

	const { showOperationToast } = useOperationNotification();

	const [isFetching, setIsFetching] = useState(false);

	const onSubmit = (data: CancelOperationFormValues) => {
		const operationId = config.getOperationId(operation);
		if (!operationId) return;

		const fio =
			mappedEmployees.find(
				(employee) => Number(employee.value.split('_')[0]) === Number(data.closing_employee_fio),
			)?.label ?? '';

		const payload = config.buildCancelPayload(operation, fio, mappedEmployees);

		let operationsForSkip = skippedOperations.map((op) =>
			config.getOperationId(op) === operationId ? config.markOperationCanceled(op, data.date) : op,
		);

		setIsFetching(true);

		const result = cancelOperation({ cancelRequest: payload });
		result
			.unwrap()
			.then((data: unknown) => {
				if (!data) throw new Error('Operation not canceled');

				return operationsForSkip;
			})
			.catch((err: unknown) => {
				console.error(err);

				// Если не удалось отменить операцию, помечаем ее как не отмененную
				operationsForSkip = skippedOperations.map((op) =>
					config.getOperationId(op) === operationId ? config.markOperationNotCanceled(op, data.date) : op,
				);

				return operationsForSkip;
			})
			.then((operationsForSkip: TOperation[]) => {
				// Если это последний шаг, то отправляем операции на обновление
				if (step >= skippedOperations.length - 1) {
					return updateOperations(operationsForSkip).then(() => operationsForSkip);
				} else {
					showOperationToast({
						status: 'success',
						type: 'cancel',
						stepId: config.getOperationStepId(operation),
						name: config.getOperationTitle(operation),
					});
				}

				return operationsForSkip;
			})
			.then((operationsForSkip: TOperation[]) => {
				setStep(step + 1);
				setSkippedOperations(operationsForSkip);
			})
			.catch((err: unknown) => {
				setError(true);
				console.error(err);
			})
			.finally(() => {
				setIsFetching(false);
			});
	};

	const onSkipOperation = () => {
		if (step === skippedOperations.length - 1) {
			updateOperations(skippedOperations);
			setStep(step + 1);
		} else {
			showOperationToast({
				status: 'success',
				type: 'skip',
				stepId: config.getOperationStepId(operation),
				name: config.getOperationTitle(operation),
			});
			setStep(step + 1);
		}
	};

	const { control, handleSubmit } = useForm<CancelOperationFormValues>({
		defaultValues: {
			closing_employee_fio:
				mappedEmployees.find((employee) => Number(employee.value.split('_')[0]) === currentUserId)?.value ??
				undefined,
		},
		resolver: yupResolver(cancelOperationSchema),
	});

	if (step === skippedOperations.length) {
		const lastOperation = skippedOperations[skippedOperations.length - 1];
		const isCanceled = config.getOperationCanceledStatus(lastOperation);
		return isCanceled ? (
			<CancelOperationAlert
				stepId={config.getOperationStepId(lastOperation)}
				operationName={config.getOperationTitle(lastOperation)}
			/>
		) : (
			<OperationUnfilled
				stepId={config.getOperationStepId(lastOperation)}
				operationName={config.getOperationTitle(lastOperation)}
			/>
		);
	}

	if (error) {
		return (
			<ErrorFillOperationAlert
				handleCompleteOperationAgain={() => setError(false)}
				productTechMapNumber={config.getTechMapNumber(techMap)}
			/>
		);
	}

	if (preview) {
		return (
			<SkippedOperationsAlertProduct
				handleSkipOperations={() => {
					setSkippedOperations([]);
					setPreview(false);
				}}
				handleCancelOperations={() => setPreview(false)}
			/>
		);
	}

	return (
		<OperationLayout
			header={
				<OperationFormHeader
					title="Отмена операции"
					techMap={
						techMap as
							| ProductTechMapResponse
							| AssemblyTechMapResponse
							| PreformTechMapResponse
							| RubberTechMapResponse
					}
				/>
			}
			footer={
				<chakra.div display="flex" flexDirection="column" gap="12px" w="100%">
					<Button variant="solid" width="100%" onClick={handleSubmit(onSubmit)} isDisabled={isFetching}>
						Отменить операцию
					</Button>
					<Button
						colorScheme="tertiary"
						variant="outline"
						width="100%"
						onClick={onSkipOperation}
						isDisabled={isFetching}
					>
						Оставить незаполненной
					</Button>
				</chakra.div>
			}
		>
			<chakra.div display="flex" flexDirection="column" gap="16px">
				<Text color="gray.800" fontSize="16px" fontWeight="400" lineHeight="24px">
					Вы пропустили операцию{' '}
					<strong>
						{config.getOperationStepId(operation)} — {config.getOperationTitle(operation)}
					</strong>
					. Для отмены выберите ответственного или заполните операцию позднее.
				</Text>
				<Controller
					name="date"
					control={control}
					render={({ field: { onChange } }) => (
						<CalendarSinglePicker
							clearable={false}
							maxDate={new Date(2050, 1)}
							defaultDate={dayjs().format('YYYY-MM-DD')}
							label="Дата упаковки и консервации"
							onSelectDate={(date) => onChange(date)}
						/>
					)}
				/>
				<Controller
					name="closing_employee_fio"
					control={control}
					render={({ field: { value, onChange }, fieldState: { error } }) => (
						<CustomSelect
							onFocus={() => setGroupVisibility(true)}
							onBlur={() => setGroupVisibility(false)}
							showSearch
							allowClear={false}
							options={mappedEmployees}
							value={value}
							onChange={(e) => onChange(e)}
							filterOption={(input, option) =>
								String(option?.label ?? '')
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							defaultValue={
								mappedEmployees.find((employee) => employee.value === value)?.label ?? undefined
							}
							label="Исполнитель"
							optionFilterProp="children"
							dropdownStyle={{ zIndex: 1400 }}
							size="large"
							className={clsx(
								styles['select'],
								styles['select-large'],
								groupVisibility && styles['select-focus'],
								error && [styles['select-invalid']],
							)}
						/>
					)}
				/>
			</chakra.div>
		</OperationLayout>
	);
}
